import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { duration, ease } from '../lib/motion'
import { industries as nicheIndustries, markets as nicheMarkets } from '../data/searchPages'
import { useFormModal } from '../context/FormModalContext'

function Eyebrow({ children }) {
  return (
    <p className="eyebrow mb-4" style={{ color: 'var(--accent)' }}>
      {children}
    </p>
  )
}

function Section({ children, className = '' }) {
  return (
    <div className={`max-w-2xl ${className}`}>{children}</div>
  )
}

export default function CaseStudy({ study, onClose }) {
  const closeBtnRef = useRef(null)
  const lastFocusedRef = useRef(null)
  const { openInquiry } = useFormModal()

  useEffect(() => {
    if (!study) return
    lastFocusedRef.current = document.activeElement
    document.body.style.overflow = 'hidden'
    const focusTimer = setTimeout(() => closeBtnRef.current?.focus(), 50)
    const onKey = (e) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = ''
      window.removeEventListener('keydown', onKey)
      clearTimeout(focusTimer)
      lastFocusedRef.current?.focus?.()
    }
  }, [study, onClose])

  return (
    <AnimatePresence>
      {study && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: duration.fast }}
          className="fixed inset-0 z-50 overflow-y-auto"
          style={{ background: 'var(--bg)' }}
          role="dialog"
          aria-modal="true"
          aria-label={`${study.name} case study`}
        >
          <motion.div
            initial={{ y: 32, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 16, opacity: 0 }}
            transition={{ duration: duration.base, ease: ease.out }}
          >
            {/* Top bar */}
            <div
              className="sticky top-0 z-10 backdrop-blur border-b"
              style={{ borderColor: 'var(--border)', background: 'color-mix(in srgb, var(--bg) 88%, transparent)' }}
            >
              <div className="container-bw flex items-center justify-between py-5">
                <p className="eyebrow">{study.category}</p>
                <button
                  ref={closeBtnRef}
                  onClick={onClose}
                  aria-label="Close case study"
                  className="h-10 w-10 rounded-full border flex items-center justify-center text-lg hover:opacity-70 transition-opacity"
                  style={{ borderColor: 'var(--border-strong)', color: 'var(--text-primary)' }}
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Hero */}
            <section className="container-bw pt-16 pb-20 md:pt-24 md:pb-28">
              <Eyebrow>{study.tagline}</Eyebrow>
              <h2
                className="font-display font-semibold leading-[0.98] tracking-tight text-[42px] sm:text-[56px] lg:text-[68px] max-w-3xl"
                style={{ color: 'var(--text-primary)' }}
              >
                {study.name}
              </h2>
              {study.niche && (
                <div className="mt-6 flex flex-wrap gap-2">
                  {study.niche.industries.map((industryId) => {
                    const industry = nicheIndustries.find((i) => i.id === industryId)
                    const market = nicheMarkets.find((m) => m.id === study.niche.market)
                    if (!industry || !market) return null
                    return (
                      <Link
                        key={industryId}
                        to={`/website-design-${market.id}-${industry.id}`}
                        className="text-xs md:text-sm px-3 py-1.5 rounded-full border hover:opacity-70 transition-opacity"
                        style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}
                      >
                        {market.label} {industry.label}
                      </Link>
                    )
                  })}
                </div>
              )}
              {study.timeline && (
                <div className="mt-10 flex flex-wrap gap-2 md:gap-3">
                  {study.timeline.map((step, i) => (
                    <div key={step} className="flex items-center gap-2 md:gap-3">
                      <span
                        className="text-xs md:text-sm px-3 py-1.5 rounded-full border"
                        style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }}
                      >
                        {step}
                      </span>
                      {i < study.timeline.length - 1 && (
                        <span style={{ color: 'var(--text-muted)' }}>→</span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {study.heroImage && (
                <div
                  className="mt-14 rounded-2xl border overflow-hidden"
                  style={{ borderColor: 'var(--border)', background: study.heroImageBg || 'var(--card)' }}
                >
                  <img src={study.heroImage} alt={`${study.name} identity`} className="w-full h-auto" />
                </div>
              )}
            </section>

            {/* Challenge */}
            <section className="container-bw py-16 border-t" style={{ borderColor: 'var(--border)' }}>
              <Section>
                <Eyebrow>The Challenge</Eyebrow>
                <p className="font-display text-2xl md:text-3xl leading-snug" style={{ color: 'var(--text-primary)' }}>
                  {study.challenge}
                </p>
              </Section>
            </section>

            {/* Overview (optional) — a single grounding paragraph, used by richer/flagship studies */}
            {study.overview && (
              <section className="container-bw py-16 border-t" style={{ borderColor: 'var(--border)' }}>
                <Section>
                  <Eyebrow>Overview</Eyebrow>
                  <p className="text-lg leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                    {study.overview}
                  </p>
                </Section>
              </section>
            )}

            {/* Narrative (optional) — an ordered set of labeled sections (e.g. Discovery, Strategy,
                Design, Motion, Outcome, Reflection) for case studies that need more structure than
                a single "Thinking" block. Falls back to the older single-block `thinking` field
                below when a study doesn't define `narrative`. */}
            {study.narrative
              ? study.narrative.map((block, i) => (
                  <section key={block.heading || i} className="container-bw py-16 border-t" style={{ borderColor: 'var(--border)' }}>
                    <Section>
                      <Eyebrow>{block.heading}</Eyebrow>
                      <div className="space-y-5">
                        {block.paragraphs.map((p, j) => (
                          <p key={j} className="text-lg leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                            {p}
                          </p>
                        ))}
                      </div>
                    </Section>
                  </section>
                ))
              : study.thinking && (
                  <section className="container-bw py-16 border-t" style={{ borderColor: 'var(--border)' }}>
                    <Section>
                      <Eyebrow>{study.thinkingLabel || 'Our Thinking'}</Eyebrow>
                      <div className="space-y-5">
                        {study.thinking.map((p, i) => (
                          <p key={i} className="text-lg leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                            {p}
                          </p>
                        ))}
                      </div>
                    </Section>
                  </section>
                )}

            {/* Evolution (optional) */}
            {study.evolution && (
              <section className="container-bw py-16 border-t" style={{ borderColor: 'var(--border)' }}>
                <Eyebrow>Design Evolution</Eyebrow>
                <div
                  className="mt-8 rounded-2xl border overflow-hidden"
                  style={{ borderColor: 'var(--border)' }}
                >
                  <img src={study.evolution} alt="Design evolution" className="w-full h-auto" />
                </div>
                <div className="mt-6 flex items-center gap-3 flex-wrap">
                  {['Initial Concept', 'Refinement', 'Final Identity'].map((label, i) => (
                    <div key={label} className="flex items-center gap-3">
                      <span className="text-sm" style={{ color: 'var(--text-muted)' }}>{label}</span>
                      {i < 2 && <span style={{ color: 'var(--text-muted)' }}>→</span>}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Why this works checklist */}
            <section className="container-bw py-16 border-t" style={{ borderColor: 'var(--border)' }}>
              <Eyebrow>Why This Works</Eyebrow>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-4 max-w-3xl">
                {study.checklist.map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <span className="mt-1 text-sm shrink-0" style={{ color: 'var(--accent)' }}>✓</span>
                    <p style={{ color: 'var(--text-secondary)' }}>{item}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Live walkthrough video(s) — real screen recordings of the actual
                live site, not a mockup. Placed right after "Why This Works"
                so the claim is immediately followed by something the visitor
                can watch, before the static showcase screenshots below.
                Autoplay+muted+loop for browsers that allow it; `controls`
                still given so a visitor can pause/scrub a longer recording. */}
            {study.previewVideos && (
              <section className="container-bw py-16 border-t text-center" style={{ borderColor: 'var(--border)' }}>
                <Eyebrow>{study.previewVideos.length > 1 ? 'See It Live' : 'Live Walkthrough'}</Eyebrow>
                <div className="mt-8 flex flex-wrap justify-center gap-8">
                  {study.previewVideos.map((v, i) => (
                    <div key={i} className="mx-auto">
                      <div
                        className="mx-auto max-w-[240px] rounded-[2rem] border-[6px] overflow-hidden"
                        style={{ borderColor: 'var(--border-strong)' }}
                      >
                        <video
                          src={v.src}
                          muted
                          loop
                          playsInline
                          controls
                          preload="metadata"
                          className="w-full h-auto block"
                        />
                      </div>
                      {v.label && (
                        <p className="mt-3 text-sm" style={{ color: 'var(--text-muted)' }}>
                          {v.label}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Site screenshots / applications */}
            {(study.showcaseImages || study.showcaseImage) && (
              <section className="container-bw py-16 border-t" style={{ borderColor: 'var(--border)' }}>
                <Eyebrow>{study.showcaseLabel || 'In Practice'}</Eyebrow>
                <div className={study.showcaseImages ? 'mt-8 grid sm:grid-cols-2 gap-6' : 'mt-8'}>
                  {(study.showcaseImages || [study.showcaseImage]).map((src, i) => (
                    <div
                      key={i}
                      className="rounded-2xl border overflow-hidden"
                      style={{ borderColor: 'var(--border)' }}
                    >
                      <img src={src} alt={`${study.name} live site`} className="w-full h-auto" />
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Mobile screenshot(s) — only shown when real mobile captures exist, never faked from a desktop crop */}
            {(study.mobileImages || study.mobileImage) && (
              <section className="container-bw py-16 border-t text-center" style={{ borderColor: 'var(--border)' }}>
                <Eyebrow>Works On Mobile Too</Eyebrow>
                <div className="mt-8 flex flex-wrap justify-center gap-6">
                  {(study.mobileImages || [study.mobileImage]).map((src, i) => (
                    <div
                      key={i}
                      className="mx-auto max-w-[240px] rounded-[2rem] border-[6px] overflow-hidden"
                      style={{ borderColor: 'var(--border-strong)' }}
                    >
                      <img src={src} alt={`${study.name} on mobile`} className="w-full h-auto block" />
                    </div>
                  ))}
                </div>
              </section>
            )}

            {study.applications && (
              <section className="container-bw py-16 border-t" style={{ borderColor: 'var(--border)' }}>
                <Eyebrow>Brand Applications</Eyebrow>
                <div className="flex flex-wrap gap-3 max-w-3xl">
                  {study.applications.map((app) => (
                    <span
                      key={app}
                      className="text-sm px-4 py-2 rounded-full border"
                      style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)' }}
                    >
                      {app}
                    </span>
                  ))}
                </div>
              </section>
            )}

            {/* Before / After */}
            <section className="container-bw py-16 border-t" style={{ borderColor: 'var(--border)' }}>
              <Eyebrow>What Changed?</Eyebrow>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl">
                <div>
                  <p className="text-sm font-medium mb-4" style={{ color: 'var(--text-muted)' }}>Before</p>
                  <ul className="space-y-3">
                    {study.before.map((item) => (
                      <li key={item} className="text-base leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-sm font-medium mb-4" style={{ color: 'var(--accent)' }}>After</p>
                  <ul className="space-y-3">
                    {study.after.map((item) => (
                      <li key={item} className="text-base leading-relaxed" style={{ color: 'var(--text-primary)' }}>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </section>

            {/* Key takeaway — py-20/28, not the py-16 used by every other
                sub-section on this page: this is the closing statement,
                deliberately given more room, same treatment as FinalCTA. */}
            <section className="container-bw py-20 md:py-28 border-t" style={{ borderColor: 'var(--border)' }}>
              <p
                className="font-display italic text-2xl md:text-4xl leading-snug max-w-3xl"
                style={{ color: 'var(--text-primary)' }}
              >
                {study.takeaway}
              </p>

              {study.liveUrl && (
                <a
                  href={`https://${study.liveUrl}`}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-10 inline-flex items-center gap-2 text-sm font-medium underline underline-offset-4"
                  style={{ color: 'var(--accent)' }}
                >
                  View live site ↗
                </a>
              )}

              {/* Phase 9 funnel connection: case studies are proof, and proof
                  should lead somewhere. Closing CTA into the real next
                  steps (Services/Pricing), not just a dead end after the
                  takeaway. */}
              <div
                className="mt-14 pt-10 border-t flex flex-wrap items-center gap-4"
                style={{ borderColor: 'var(--border)' }}
              >
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Want something like this for your business?
                </p>
                <button
                  onClick={() => openInquiry({})}
                  className="text-sm font-medium px-5 py-2.5 rounded-full"
                  style={{ background: 'var(--accent)', color: 'var(--bg)' }}
                >
                  Get a Free Homepage Concept
                </button>
                <Link to="/services" className="text-sm font-medium underline underline-offset-4" style={{ color: 'var(--accent)' }}>
                  See Services
                </Link>
              </div>
            </section>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
